import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Eye, EyeOff, Mail, Lock, Building2, FileText, User, CheckCircle, Instagram, Facebook, Youtube, Film, Plus, Trash2, LinkIcon, ShieldCheck } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { companyApi, legalApi } from '../lib/api';
import { toast } from 'sonner';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import PolicyConsentCheckbox from '../components/PolicyConsentCheckbox';

const CreatorRegisterPage = () => {
  const [step, setStep] = useState(1);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { register, verifyEmail, resendCode, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  // Step 1: Account
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Step 2: Verification
  const [verificationCode, setVerificationCode] = useState('');
  const [needsVerification, setNeedsVerification] = useState(false);

  // Step 2: Creator Profile
  const [companyName, setCompanyName] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [contentTypeDescription, setContentTypeDescription] = useState('');
  const [description, setDescription] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [instagram, setInstagram] = useState('');
  const [facebook, setFacebook] = useState('');
  const [youtube, setYoutube] = useState('');
  const [sampleFilms, setSampleFilms] = useState([]);
  const [policyAccepted, setPolicyAccepted] = useState(false);
  const [acceptedPolicies, setAcceptedPolicies] = useState([]);
  const [showPolicyError, setShowPolicyError] = useState(false);

  const handleAccountSubmit = async (e) => {
    e.preventDefault();
    if (!policyAccepted) {
      setShowPolicyError(true);
      return;
    }
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setLoading(true);
    try {
      const result = await register(email, password, firstName || 'Creator');
      setContactEmail(email);
      // Record policy consent
      if (result.user_id) {
        try { await legalApi.recordRegistrationConsent({ user_id: result.user_id, policies: acceptedPolicies }); } catch {}
      }
      if (result.requires_verification) {
        setNeedsVerification(true);
        setStep(2);
        toast.success('Check your email for a verification code!');
      } else {
        setStep(3);
      }
    } catch (error) {
      toast.error(error.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifySubmit = async (e) => {
    e.preventDefault();
    if (verificationCode.length !== 6) {
      toast.error('Please enter the 6-digit code');
      return;
    }
    setLoading(true);
    try {
      await verifyEmail(email, verificationCode);
      toast.success('Email verified!');
      setStep(3); // Go to profile step
    } catch (error) {
      toast.error(error.message || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    try {
      await resendCode(email);
      toast.success('New code sent to your email');
    } catch (error) {
      toast.error(error.message || 'Failed to resend code');
    }
  };

  const handleCompanySubmit = async (e) => {
    e.preventDefault();
    if (!companyName || !firstName || !lastName || !contentTypeDescription || !contactEmail) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (!instagram && !facebook && !youtube) {
      toast.error('Please provide at least one social profile (Instagram, Facebook, or YouTube)');
      return;
    }
    setLoading(true);
    try {
      await companyApi.register({
        name: companyName,
        first_name: firstName,
        last_name: lastName,
        description: description || contentTypeDescription,
        content_type_description: contentTypeDescription,
        contact_email: contactEmail,
        instagram: instagram || null,
        facebook: facebook || null,
        youtube: youtube || null,
        sample_films: sampleFilms.filter(sf => sf.title && (sf.youtube_id || sf.description)),
      });
      setStep(4);
    } catch (error) {
      toast.error(error.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const addSampleFilm = () => {
    if (sampleFilms.length >= 3) {
      toast.error('Maximum 3 sample works');
      return;
    }
    setSampleFilms([...sampleFilms, { title: '', description: '', youtube_id: '' }]);
  };

  const updateSampleFilm = (index, field, value) => {
    const updated = [...sampleFilms];
    updated[index][field] = value;
    setSampleFilms(updated);
  };

  const removeSampleFilm = (index) => {
    setSampleFilms(sampleFilms.filter((_, i) => i !== index));
  };

  React.useEffect(() => {
    if (isAuthenticated && step === 1) {
      setStep(3); // Skip account + verification, go to profile
    }
  }, [isAuthenticated, step]);

  return (
    <div className="min-h-screen bg-[#030014] flex items-center justify-center px-4 py-12" data-testid="creator-register-page">
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#8B5CF6]/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-[#00FF9D]/5 rounded-full blur-3xl" />
      </div>

      <Link to="/" className="absolute top-6 left-6 z-10">
        <img src="/reframes-logo.png" alt="reframes" className="h-9 md:h-11" />
      </Link>

      {/* Progress Steps */}
      <div className="absolute top-6 right-6 z-10 hidden md:flex items-center gap-4">
        {[
          { num: 1, label: 'Account' },
          { num: 2, label: 'Verify' },
          { num: 3, label: 'Profile' },
          { num: 4, label: 'Done' },
        ].map((s) => (
          <div key={s.num} className="flex items-center gap-2">
            <div className="flex flex-col items-center">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                  step >= s.num ? 'bg-[#8B5CF6] text-white' : 'bg-white/10 text-gray-400'
                }`}
              >
                {step > s.num ? <CheckCircle className="w-5 h-5" /> : s.num}
              </div>
              <span className="text-xs text-gray-500 mt-1">{s.label}</span>
            </div>
            {s.num < 4 && <div className={`w-8 h-0.5 mb-5 ${step > s.num ? 'bg-[#8B5CF6]' : 'bg-white/10'}`} />}
          </div>
        ))}
      </div>

      <div className="relative z-10 w-full max-w-2xl">
        {/* Step 1: Account */}
        {step === 1 && (
          <div className="admin-card p-8 max-w-lg mx-auto">
            <div className="text-center mb-8">
              <div className="w-16 h-16 mx-auto mb-4 rounded-xl bg-[#8B5CF6]/20 flex items-center justify-center text-[#8B5CF6]">
                <Building2 className="w-8 h-8" />
              </div>
              <h1 className="text-2xl md:text-3xl font-bold mb-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                Become a Creator
              </h1>
              <p className="text-gray-400">Step 1: Create your account</p>
            </div>

            <form onSubmit={handleAccountSubmit} className="space-y-4">
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} required className="pl-10 bg-[#030014] border-[#8B5CF6]/20 h-12" data-testid="email-input" />
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input type={showPassword ? 'text' : 'password'} placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} className="pl-10 pr-10 bg-[#030014] border-[#8B5CF6]/20 h-12" data-testid="password-input" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white">
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input type={showPassword ? 'text' : 'password'} placeholder="Confirm Password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required minLength={6} className="pl-10 bg-[#030014] border-[#8B5CF6]/20 h-12" data-testid="confirm-password-input" />
              </div>
              <PolicyConsentCheckbox
                accepted={policyAccepted}
                onChange={(checked, policies) => { setPolicyAccepted(checked); setAcceptedPolicies(policies); if (checked) setShowPolicyError(false); }}
                hasError={showPolicyError}
              />
              <Button type="submit" disabled={loading} className="w-full btn-primary h-12 text-base" data-testid="continue-button">
                {loading ? <div className="spinner w-5 h-5" /> : 'Continue'}
              </Button>
            </form>

            <p className="text-center mt-6 text-gray-400">
              Already have an account?{' '}
              <Link to="/login" className="text-[#8B5CF6] hover:text-[#A78BFA] font-medium">Sign In</Link>
            </p>
          </div>
        )}

        {/* Step 2: Email Verification */}
        {step === 2 && (
          <div className="admin-card p-8 max-w-lg mx-auto">
            <div className="text-center mb-8">
              <div className="w-16 h-16 mx-auto mb-4 rounded-xl bg-[#8B5CF6]/20 flex items-center justify-center text-[#8B5CF6]">
                <ShieldCheck className="w-8 h-8" />
              </div>
              <h1 className="text-2xl md:text-3xl font-bold mb-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                Verify Your Email
              </h1>
              <p className="text-gray-400">We sent a 6-digit code to <span className="text-white font-medium">{email}</span></p>
            </div>

            <form onSubmit={handleVerifySubmit} className="space-y-4">
              <div>
                <Input
                  type="text"
                  placeholder="Enter 6-digit code"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  maxLength={6}
                  className="bg-[#030014] border-[#8B5CF6]/20 h-14 text-center text-2xl tracking-[0.5em] font-mono"
                  data-testid="verification-code-input"
                />
              </div>
              <Button type="submit" disabled={loading || verificationCode.length !== 6} className="w-full btn-primary h-12 text-base" data-testid="verify-button">
                {loading ? <div className="spinner w-5 h-5" /> : 'Verify & Continue'}
              </Button>
            </form>

            <p className="text-center mt-6 text-gray-400">
              Didn't receive the code?{' '}
              <button onClick={handleResendCode} className="text-[#8B5CF6] hover:text-[#A78BFA] font-medium" data-testid="resend-code-button">Resend Code</button>
            </p>
          </div>
        )}

        {/* Step 3: Creator Profile */}
        {step === 3 && (
          <div className="admin-card p-8">
            <div className="text-center mb-8">
              <div className="w-16 h-16 mx-auto mb-4 rounded-xl bg-[#8B5CF6]/20 flex items-center justify-center text-[#8B5CF6]">
                <FileText className="w-8 h-8" />
              </div>
              <h1 className="text-2xl md:text-3xl font-bold mb-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                Creator Profile
              </h1>
              <p className="text-gray-400">Tell us about yourself and your content</p>
            </div>

            <form onSubmit={handleCompanySubmit} className="space-y-6">
              {/* Studio Name */}
              <div>
                <label className="text-sm text-gray-400 mb-1.5 block">Studio / Channel Name *</label>
                <p className="text-xs text-gray-500 mb-2">This is the name users will see on the platform</p>
                <div className="relative">
                  <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input type="text" placeholder="e.g. NorthStar AI Studios" value={companyName} onChange={(e) => setCompanyName(e.target.value)} required className="pl-10 bg-[#030014] border-[#8B5CF6]/20 h-12" data-testid="company-name-input" />
                </div>
              </div>

              {/* First & Last Name */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-gray-400 mb-1.5 block">First Name *</label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input type="text" placeholder="First name" value={firstName} onChange={(e) => setFirstName(e.target.value)} required className="pl-10 bg-[#030014] border-[#8B5CF6]/20 h-12" data-testid="first-name-input" />
                  </div>
                </div>
                <div>
                  <label className="text-sm text-gray-400 mb-1.5 block">Last Name *</label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input type="text" placeholder="Last name" value={lastName} onChange={(e) => setLastName(e.target.value)} required className="pl-10 bg-[#030014] border-[#8B5CF6]/20 h-12" data-testid="last-name-input" />
                  </div>
                </div>
              </div>

              {/* Contact Email */}
              <div>
                <label className="text-sm text-gray-400 mb-1.5 block">Contact Email *</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input type="email" placeholder="your@email.com" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} required className="pl-10 bg-[#030014] border-[#8B5CF6]/20 h-12" data-testid="contact-email-input" />
                </div>
              </div>

              {/* Content Type */}
              <div>
                <label className="text-sm text-gray-400 mb-1.5 block">Type of Content *</label>
                <p className="text-xs text-gray-500 mb-2">Describe the kind of AI-generated content you create</p>
                <div className="relative">
                  <Film className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                  <Textarea
                    placeholder="e.g. I create AI-generated short films using Runway Gen-3 and Kling, focusing on sci-fi narratives and visual effects..."
                    value={contentTypeDescription}
                    onChange={(e) => setContentTypeDescription(e.target.value)}
                    required
                    className="pl-10 bg-[#030014] border-[#8B5CF6]/20 min-h-[100px]"
                    data-testid="content-type-input"
                  />
                </div>
              </div>

              {/* About / Bio */}
              <div>
                <label className="text-sm text-gray-400 mb-1.5 block">About You / Bio (optional)</label>
                <Textarea
                  placeholder="Tell us more about your background, experience, and creative vision..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="bg-[#030014] border-[#8B5CF6]/20 min-h-[80px]"
                  data-testid="description-input"
                />
              </div>

              {/* Social Links */}
              <div>
                <label className="text-sm text-gray-400 mb-1.5 block">Social Profiles *</label>
                <p className="text-xs text-gray-500 mb-3">At least one social profile is required — helps us verify your work</p>
                <div className="space-y-3">
                  <div className="relative">
                    <Instagram className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-pink-400" />
                    <Input type="url" placeholder="https://instagram.com/yourprofile" value={instagram} onChange={(e) => setInstagram(e.target.value)} className="pl-10 bg-[#030014] border-[#8B5CF6]/20 h-11" data-testid="instagram-input" />
                  </div>
                  <div className="relative">
                    <Facebook className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-blue-400" />
                    <Input type="url" placeholder="https://facebook.com/yourpage" value={facebook} onChange={(e) => setFacebook(e.target.value)} className="pl-10 bg-[#030014] border-[#8B5CF6]/20 h-11" data-testid="facebook-input" />
                  </div>
                  <div className="relative">
                    <Youtube className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-red-400" />
                    <Input type="url" placeholder="https://youtube.com/@yourchannel" value={youtube} onChange={(e) => setYoutube(e.target.value)} className="pl-10 bg-[#030014] border-[#8B5CF6]/20 h-11" data-testid="youtube-input" />
                  </div>
                </div>
              </div>

              {/* Sample Content */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-sm text-gray-400">Sample Content (optional)</label>
                  <span className="text-xs text-gray-500">{sampleFilms.length}/3</span>
                </div>
                <p className="text-xs text-gray-500 mb-3">Upload examples of your work to strengthen your application</p>

                {sampleFilms.map((sf, index) => (
                  <div key={index} className="mb-3 p-4 bg-[#0a0a1f] rounded-lg border border-white/5">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-medium text-gray-300">Sample {index + 1}</span>
                      <button type="button" onClick={() => removeSampleFilm(index)} className="text-red-400 hover:text-red-300 p-1">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="space-y-2">
                      <Input
                        placeholder="Title of the work"
                        value={sf.title}
                        onChange={(e) => updateSampleFilm(index, 'title', e.target.value)}
                        className="bg-[#030014] border-white/10 h-10 text-sm"
                        data-testid={`sample-title-${index}`}
                      />
                      <Input
                        placeholder="YouTube Video ID (e.g. dQw4w9WgXcQ)"
                        value={sf.youtube_id}
                        onChange={(e) => updateSampleFilm(index, 'youtube_id', e.target.value)}
                        className="bg-[#030014] border-white/10 h-10 text-sm"
                        data-testid={`sample-youtube-${index}`}
                      />
                      <Textarea
                        placeholder="Brief description of this work..."
                        value={sf.description}
                        onChange={(e) => updateSampleFilm(index, 'description', e.target.value)}
                        className="bg-[#030014] border-white/10 min-h-[60px] text-sm"
                        data-testid={`sample-description-${index}`}
                      />
                      {sf.youtube_id && (
                        <div className="aspect-video rounded overflow-hidden mt-2">
                          <iframe
                            src={`https://www.youtube.com/embed/${sf.youtube_id}`}
                            title={sf.title || 'Sample'}
                            className="w-full h-full"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope"
                            allowFullScreen
                          />
                        </div>
                      )}
                    </div>
                  </div>
                ))}

                {sampleFilms.length < 3 && (
                  <Button type="button" variant="outline" onClick={addSampleFilm} className="w-full border-dashed border-white/20 text-gray-400 hover:text-white hover:border-[#8B5CF6]/40" data-testid="add-sample-btn">
                    <Plus className="w-4 h-4 mr-2" /> Add Sample Work
                  </Button>
                )}
              </div>

              <Button type="submit" disabled={loading} className="w-full btn-primary h-12 text-base" data-testid="submit-button">
                {loading ? <div className="spinner w-5 h-5" /> : 'Submit Application'}
              </Button>
            </form>
          </div>
        )}

        {/* Step 4: Pending Approval */}
        {step === 4 && (
          <div className="admin-card p-8 text-center max-w-lg mx-auto">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#00FF9D]/20 flex items-center justify-center">
              <CheckCircle className="w-10 h-10 text-[#00FF9D]" />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Application Submitted!
            </h1>
            <p className="text-gray-300 mb-6">Thank you for applying to become a creator on reframes.</p>
            <div className="bg-[#8B5CF6]/10 rounded-lg p-6 mb-8 text-left">
              <h3 className="font-semibold mb-3 text-[#8B5CF6]">What happens next?</h3>
              <ul className="space-y-2 text-sm text-gray-300">
                <li className="flex items-start gap-2"><span className="text-[#00FF9D] mt-1">1.</span>Our team will review your application</li>
                <li className="flex items-start gap-2"><span className="text-[#00FF9D] mt-1">2.</span>You'll receive an email once approved</li>
                <li className="flex items-start gap-2"><span className="text-[#00FF9D] mt-1">3.</span>Access your creator dashboard to upload content</li>
                <li className="flex items-start gap-2"><span className="text-[#00FF9D] mt-1">4.</span>Your content will be reviewed before going live</li>
              </ul>
            </div>
            <div className="space-y-3">
              <Button onClick={() => navigate('/browse')} className="w-full btn-primary" data-testid="browse-button">Browse Content While You Wait</Button>
              <Button onClick={() => navigate('/company/dashboard')} variant="outline" className="w-full border-[#8B5CF6]/30" data-testid="dashboard-button">Check Application Status</Button>
            </div>
          </div>
        )}

        {step < 4 && (
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-500">
              Just want to watch content?{' '}
              <Link to="/signup" className="text-[#8B5CF6] hover:underline">Sign up as a viewer</Link>
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreatorRegisterPage;
